import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export type NotificationChannel = 'in_app' | 'email' | 'sms' | 'web_push';
export type SeverityMin = 'critical' | 'warning' | 'info';

export interface QuietHours {
  start: string;
  end: string;
  timezone: string;
}

export interface NotificationPreference {
  id: string;
  user_id: string;
  property_id: string | null;
  channel: NotificationChannel;
  severity_min: SeverityMin;
  enabled: boolean;
  email_override: string | null;
  phone_for_sms: string | null;
  sms_consent_at: string | null;
  critical_opt_out_acknowledged_at: string | null;
  quiet_hours?: QuietHours | null;
  created_at: string;
  updated_at: string;
}

/** Fetch notification preferences for the current user, optionally scoped to a property. */
export function useNotificationPreferences(propertyId: string | null) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: preferences = [], isLoading, error, refetch } = useQuery({
    queryKey: ['notification_preferences', user?.id ?? '', propertyId ?? 'all'],
    queryFn: async () => {
      if (!user?.id) return [];

      let q = supabase
        .from('notification_preferences')
        .select('*')
        .eq('user_id', user.id);

      if (propertyId) {
        q = q.or(`property_id.eq.${propertyId},property_id.is.null`);
      }

      const { data, error: e } = await q.order('channel');
      if (e) throw e;
      return (data ?? []) as NotificationPreference[];
    },
    enabled: !!user?.id,
  });

  const upsertMutation = useMutation({
    mutationFn: async (row: {
      channel: NotificationChannel;
      property_id: string | null;
      severity_min: SeverityMin;
      enabled: boolean;
      email_override?: string | null;
      phone_for_sms?: string | null;
      critical_opt_out_acknowledged_at?: string | null;
      sms_consent_at?: string | null;
      quiet_hours?: QuietHours | null;
    }) => {
      if (!user?.id) throw new Error('Not authenticated');
      const payload: Record<string, unknown> = {
        user_id: user.id,
        property_id: row.property_id,
        channel: row.channel,
        severity_min: row.severity_min,
        enabled: row.enabled,
        email_override: row.email_override ?? null,
        phone_for_sms: row.phone_for_sms ?? null,
        updated_at: new Date().toISOString(),
      };
      if (row.critical_opt_out_acknowledged_at !== undefined) {
        payload.critical_opt_out_acknowledged_at = row.critical_opt_out_acknowledged_at;
      }
      if (row.sms_consent_at !== undefined) {
        payload.sms_consent_at = row.sms_consent_at;
      }
      if (row.quiet_hours !== undefined) {
        payload.quiet_hours = row.quiet_hours;
      }
      const { error } = await supabase.from('notification_preferences').upsert(payload, { onConflict: 'user_id,property_id,channel' });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification_preferences', user?.id ?? '', propertyId ?? 'all'] });
    },
  });

  return {
    preferences,
    isLoading,
    error,
    refetch,
    upsert: upsertMutation.mutateAsync,
    isUpserting: upsertMutation.isPending,
  };
}
